import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export default function SystemVersionManager() {
  const [currentVersion, setCurrentVersion] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [tablesExist, setTablesExist] = useState(false);

  useEffect(() => {
    checkTablesAndFetchData();
  }, []);

  const checkTablesAndFetchData = async () => {
    try {
      // First, try to check if the tables exist by attempting a simple query
      const { error: versionError } = await supabase?.from('system_config')?.select('config_value')?.eq('config_key', 'system_version')?.single();
      
      if (versionError && versionError?.code === 'PGRST205') {
        // Tables don't exist - show migration instructions
        setTablesExist(false);
        setCurrentVersion('1.0');
        setNotifications([]);
        setIsLoading(false);
        return;
      }
      
      // Tables exist, proceed with normal functionality
      setTablesExist(true);
      await fetchSystemVersion();
      await fetchNotifications();
    } catch (error) {
      // Network or other errors - graceful fallback
      setTablesExist(false);
      setCurrentVersion('1.0');
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSystemVersion = async () => {
    try {
      const { data, error } = await supabase?.from('system_config')?.select('config_value')?.eq('config_key', 'system_version')?.single();
      
      if (error) {
        setCurrentVersion('1.0');
        return;
      }
      
      setCurrentVersion(data?.config_value || '1.0');
    } catch (error) {
      setCurrentVersion('1.0');
    }
  };

  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabase?.from('system_notifications')?.select('*')?.eq('status', 'active')?.order('created_at', { ascending: false })?.limit(5);
      
      if (error) {
        setNotifications([]);
        return;
      }
      
      setNotifications(data || []);
    } catch (error) {
      setNotifications([]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
        <span className="text-xs text-gray-500">Loading...</span>
      </div>
    );
  }

  // If tables don't exist, show migration notice
  if (!tablesExist) {
    return (
      <div className="relative">
        <div className="flex items-center space-x-3">
          <div className="text-xs text-gray-500">
            v{currentVersion}
          </div>
          
          <button
            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
            className="relative p-1 text-orange-400 hover:text-orange-600 transition-colors"
            title="Database setup required"
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full text-xs"></span>
          </button>
        </div>

        {isNotificationOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Database Setup Required</h3>
            </div>
            
            <div className="p-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-orange-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-orange-800">Migration Required</h4>
                    <p className="text-sm text-orange-700 mt-1">
                      The system notifications tables need to be created in your database.
                    </p>
                    <div className="mt-3 text-xs text-orange-600">
                      <p><strong>Steps to fix:</strong></p>
                      <ol className="list-decimal list-inside mt-1 space-y-1">
                        <li>Run: <code className="bg-orange-100 px-1 rounded">supabase db push</code></li>
                        <li>Or apply migration manually in Supabase dashboard</li>
                        <li>File: <code className="bg-orange-100 px-1 rounded">20250925104400_add_system_version_and_notifications.sql</code></li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Version Display */}
      <div className="flex items-center space-x-3">
        <div className="text-xs text-gray-500">
          v{currentVersion}
        </div>
        
        {/* Notification Bell */}
        <button
          onClick={() => setIsNotificationOpen(!isNotificationOpen)}
          className="relative p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 17h5l-5 5-5-5h5v-4a3 3 0 00-6 0v4" 
            />
          </svg>
          
          {notifications?.length > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs"></span>
          )}
        </button>
      </div>
      {/* Notifications Dropdown */}
      {isNotificationOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">System Updates</h3>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {notifications?.length > 0 ? (
              notifications?.map((notification) => (
                <div key={notification?.id} className="p-4 border-b border-gray-100 hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        notification?.notification_type === 'update' ? 'bg-blue-500' :
                        notification?.notification_type === 'feature' ? 'bg-green-500' :
                        notification?.notification_type === 'maintenance'? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{notification?.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{notification?.message}</p>
                      {notification?.version_related && (
                        <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded">
                          v{notification?.version_related}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">
                No new updates
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}